import { useEffect, useRef, useState } from 'react';

/**
 * Backend-sort orchestration for a react-bootstrap-table grid.
 *
 * Owns the sort column/direction, hands back the `remote` / `onTableChange` /
 * `defaultSorted` props the table needs, and fires `onSortChange(column,
 * direction)` — a page-1 refetch — whenever the sort actually changes.
 *
 * @param {string} defaultSortColumn column dataField, sent verbatim as sort_column
 * @param {'asc'|'desc'} defaultSortDirection
 * @param {(column: string, direction: string) => void} onSortChange
 */
export function useRemoteTableSort(defaultSortColumn, defaultSortDirection, onSortChange) {
  const [sortColumn, setSortColumn] = useState(defaultSortColumn);
  const [sortDirection, setSortDirection] = useState(defaultSortDirection);
  const isInitialSortRender = useRef(true);
  const onSortChangeRef = useRef(onSortChange);

  useEffect(() => {
    onSortChangeRef.current = onSortChange;
  }, [onSortChange]);

  const handleTableChange = (type, { sortField, sortOrder }) => {
    if (type !== 'sort') return;
    // Ignore the library's mount-time re-fire of the current sort (the table
    // remounts behind the loading skeleton on every fetch) so we don't trigger
    // a redundant request or fight the caret.
    if (sortField === sortColumn && sortOrder === sortDirection) return;
    setSortColumn(sortField);
    setSortDirection(sortOrder);
  };

  useEffect(() => {
    if (isInitialSortRender.current) {
      isInitialSortRender.current = false;
      return;
    }
    onSortChangeRef.current?.(sortColumn, sortDirection);
  }, [sortColumn, sortDirection]);

  return {
    sortColumn,
    sortDirection,
    remote: { sort: true },
    onTableChange: handleTableChange,
    defaultSorted: [{ dataField: sortColumn, order: sortDirection }],
  };
}

export default useRemoteTableSort;
